import { PdfGen, PdfSize, PdfUnit, PdfRect, PdfBrush, PdfColor, ETextAlign } from '../../pdf/pdf';
import { ILayoutImageSingleGen } from '../layoutImageSingle';
import { IItemCount } from '@educraft/base';
import { OptsImageSingle } from '../optsImageSingle';
import { GUtil } from '../../pdf/gutil';
import { GCalc } from '../../pdf/gcalc';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/image';

const COLORS: PdfColor[] = [
  PdfColor.fromRgb(204, 51, 0),
  PdfColor.fromRgb(255, 204, 0),
  PdfColor.fromRgb(0, 168, 89),
  PdfColor.fromRgb(0, 152, 218),
  PdfColor.fromRgb(247, 173, 175),
  PdfColor.fromRgb(0, 0, 0),
];

/**
 * Priklady__Barvy — colour-coding coloring activity.
 *
 * Top row: 6 coloured circles each labelled with an answer value.
 * Grid: 3 rows × 2 cols — each cell shows an image (left half) and
 *   5 random question texts (right half) each beside an empty box.
 *   Student colours the box with the matching answer colour.
 *
 * Port of: EduFrame.ImgSingle.Generators.Priklady__Barvy
 */
export class PrirazeniBarvy implements ILayoutImageSingleGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 1;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsImageSingle): Promise<void> {
    const totalCount = Math.min(opts.list.length, this.countMax());
    const itemsPerPage = 6;

    for (let pIdx = 0; pIdx < Math.ceil(totalCount / itemsPerPage); pIdx++) {
      const page = pdf.addPage(); // portrait A4

      const {A4} = PdfSize;
      const margin1 = PdfUnit.FromMillimeters(10);
      const margin2 = PdfUnit.FromMillimeters(5);
      const margin3 = PdfUnit.FromMillimeters(1);
      const boxSize = PdfUnit.FromMillimeters(10);

      const colorRect = new PdfRect(
        margin1,
        margin1,
        A4.width - 2 * margin1,
        PdfUnit.FromMillimeters(20)
      );
      const contentRect = new PdfRect(
        colorRect.x,
        colorRect.bottom,
        colorRect.width,
        A4.height - 2 * margin1 - colorRect.height
      );

      // Gather questions grouped by answer
      const questions = new Map<string, Set<string>>();
      for (let i = 0; i < 10_000; i++) {
        const item = opts.generator.generate(opts.data);
        const key = item.answer;
        const value = item.texts.join(' ').replace(/=\s*$/, '').trim();
        if (!questions.has(key)) questions.set(key, new Set());
        questions.get(key)!.add(value);
      }

      if (questions.size < 6) return; // not enough answer variety

      const optimized = Array.from(questions.entries()).map(([key, vals]) => ({
        key,
        questions: Array.from(vals),
      }));

      // Select 6 answer groups
      let list: { key: string; questions: string[] }[];
      if (questions.size > 12) {
        const top = optimized
          .sort((a, b) => b.questions.length - a.questions.length)
          .slice(0, Math.floor(questions.size / 2));
        list = GUtil.genMap(opts.data.random, top.length)
          .map((i) => top[i])
          .slice(0, 6);
      } else {
        list = GUtil.genMap(opts.data.random, optimized.length)
          .map((i) => optimized[i])
          .slice(0, 6);
      }

      // Coloured circles with answer text
      const font1 = page.adjustFont(
        colorRect.height,
        list.map((i) => i.key),
        GUtil.baseFont
      );
      for (let i = 0; i < 6; i++) {
        const color = COLORS[i];
        const rect = new PdfRect(
          colorRect.x + (i * colorRect.width) / 6,
          colorRect.y,
          colorRect.width / 6,
          colorRect.height
        );
        const r = GCalc.centerRect(rect);
        page.drawEllipse(null, new PdfBrush(color), r);
        page.drawString(list[i].key, font1, PdfBrush.White, null, r, ETextAlign.Center);
      }

      // 3 rows × 2 cols grid
      const start = pIdx * itemsPerPage;
      const end = Math.min(start + itemsPerPage, totalCount);
      const pageItems = opts.list.slice(start, end);

      for (let j = 0; j < itemsPerPage; j++) {
        const y = Math.floor(j / 2);
        const x = j % 2;
        const rect = new PdfRect(
          contentRect.x + (x * contentRect.width) / 2,
          contentRect.y + (y * contentRect.height) / 3,
          contentRect.width / 2,
          contentRect.height / 3
        );

        const imageRect = new PdfRect(
          rect.x + margin3,
          rect.y + margin3,
          rect.width / 2 - margin3 * 2,
          rect.height - margin3 * 2
        );
        const listRect = new PdfRect(
          rect.x + imageRect.width,
          rect.y,
          rect.width - imageRect.width,
          rect.height
        );
        const innerRect = new PdfRect(
          listRect.x,
          listRect.y + margin2,
          listRect.width,
          listRect.height - margin2 * 2
        );

        if (j < pageItems.length) {
          const imgItem = pageItems[j];
          if (imgItem?.image) {
            page.drawImageCenter(imgItem.image, imageRect);
          }
        }

        // One random question from each of 5 randomly chosen groups
        const shuffledList = GUtil.genMap(opts.data.random, list.length).map((i) => list[i]);
        const innerList = shuffledList
          .map((p) => p.questions[opts.data.random.next(0, p.questions.length)])
          .slice(0, 5);

        const font2 = page.adjustFont(innerRect.width - boxSize, innerList, GUtil.baseFont);

        for (let k = 0; k < 5; k++) {
          const r = new PdfRect(
            innerRect.x,
            innerRect.y + (k * innerRect.height) / 5,
            innerRect.width,
            innerRect.height / 5
          );
          const leftRect = new PdfRect(r.x, r.y, r.width - boxSize, r.height);
          const boxRect = new PdfRect(
            r.x + leftRect.width,
            r.y + (r.height - boxSize) / 2,
            boxSize,
            boxSize
          );

          page.drawRectangle(GUtil.basePen, null, boxRect);
          page.drawString(
            innerList[k],
            font2,
            PdfBrush.Black,
            null,
            leftRect,
            ETextAlign.CenterRight
          );
        }
      }
    }
  }
}
